import React, {useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {usePageTitle} from '../../hooks/usePageTitle';
import {PageTitleKeys} from '../../config/routeConstants';
import {
    OcrBatchBox,
    OcrBatchCard,
    OcrBatchContentArea,
    OcrBatchFileInfo,
    OcrBatchFileList,
    OcrBatchFileListItem,
    OcrBatchFileTypeBadge,
    OcrBatchHeader,
    OcrBatchMainContainer,
    OcrBatchPrimaryButton,
    OcrBatchSecondaryButton,
} from "./OcrBatch.styled";
import FileDropZone from "./components/FileDropZone";
import {useOcrBatch} from "../../contexts/OcrBatchContext";
import {useNavigate} from "react-router-dom";
import {DEFAULT_OCR_OPTIONS, OcrMode, OcrOptions} from "../../models/ocr.models";
import OptionsPanel from "./components/OptionsPanel";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import CloseIcon from '@mui/icons-material/Close';
import {CircularProgress, IconButton, Tooltip} from "@mui/material";
import FilePreviewDialog from "./components/FilePreviewDialog";
import {useSnackbar} from "../../contexts/SnackbarContext";
import {
    formatOcrFileSize,
    getOcrFileTypeBadge,
    isOcrFilePreviewable,
    MAX_OCR_FILES_COUNT,
    OCR_FILE_SIZE_LIMITS,
    OCR_SIZE_MB
} from "../../utils/ocr-utils";

// TODO: poprawić widok dla mniejszego ekranu, Header i FileZone
const OcrBatch = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {startBatch} = useOcrBatch();

    usePageTitle(PageTitleKeys.OcrBatch);

    const fileInputRef = useRef<HTMLInputElement>(null);

    const [isDragging, setIsDragging] = useState(false);

    const [files, setFiles] = useState<File[]>([]);
    const [previewFile, setPreviewFile] = useState<File | null>(null);
    const [options, setOptions] = useState<OcrOptions>(DEFAULT_OCR_OPTIONS);

    const { showSnackbar } = useSnackbar();
    const [isLoading, setIsLoading] = useState(false);

    const addFiles = (newFiles: File[]) => {
        const oversizedFiles = newFiles.filter(file => {
            const limit = file.type === 'application/pdf' ? OCR_FILE_SIZE_LIMITS.pdf : OCR_FILE_SIZE_LIMITS.image;
            return file.size > limit * OCR_SIZE_MB;
        });

        const validFiles = newFiles.filter(file => {
            const limit = file.type === 'application/pdf' ? OCR_FILE_SIZE_LIMITS.pdf : OCR_FILE_SIZE_LIMITS.image;
            return file.size <= limit * OCR_SIZE_MB;
        });

        // TODO: to change warning - np. pliki za duże, nie zostały dodane
        if (oversizedFiles.length > 0) {
            showSnackbar(
                t('ocr.messages.fileTooLargeWarning',
                    { maxSize: 50 }
                ),
                'warning'
            );
        }

        setFiles(prev => {
            const existingNamesSet = new Set(prev.map(p => p.name));
            const acceptedFiles = validFiles.filter(file => !existingNamesSet.has(file.name));

            // TODO
            if (prev.length + acceptedFiles.length > MAX_OCR_FILES_COUNT) {
                showSnackbar(
                    t('ocr.messages.fileLimitReachedWarning',
                        { maxFiles: MAX_OCR_FILES_COUNT }
                    ),
                    'warning',
                );

                return [...prev, ...acceptedFiles.slice(0, MAX_OCR_FILES_COUNT - prev.length)];
            }

            return [...prev, ...acceptedFiles];
        });
    };

    const handleRemoveFile = (fileName: string) => {
        setFiles(prev => prev.filter(file => file.name !== fileName));
    };

    const handleChangeOption = (mode: OcrMode) => {
        setOptions(prev => ({...prev, mode}));
    };

    const handleProcessFiles = async () => {
        if (files.length === 0) return;

        setIsLoading(true);
        try {
            const batchId = await startBatch(files, options);
            navigate(`/ocr/batch/${batchId}`);
        } catch (error) {
            console.error('Error processing files:', error);
            showSnackbar(t('ocr.messages.fileScanError'), 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleRemoveAllFiles = () => {
        if (files.length === 0) return;
        setFiles([]);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.length) {
            addFiles(Array.from(e.target.files));
            e.target.value = '';
        }
    };

    const onDrop = (e: React.DragEvent) => {
        e.preventDefault();

        setIsDragging(false);
        if (e.dataTransfer.files?.length) {
            addFiles(Array.from(e.dataTransfer.files));
        }
    };

//TODO: add info about files number and size limtis in file drop zones
    return (
        <OcrBatchCard>
            <OcrBatchMainContainer>
                <OcrBatchHeader>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*,application/pdf"
                        multiple
                        style={{display: 'none'}}
                        onChange={handleInputChange}
                        aria-hidden="true"
                        tabIndex={-1}
                    />

                    <OptionsPanel
                        mode={options.mode}
                        onChangeOption={handleChangeOption}
                    />

                    <OcrBatchBox>
                        <OcrBatchSecondaryButton
                            variant="outlined"
                            onClick={handleRemoveAllFiles}
                            disabled={files.length === 0 || isLoading}
                            aria-disabled={files.length === 0 || isLoading}
                            aria-label={t('ocr.button.removeAllFiles')}
                        >
                            {t('ocr.button.removeAllFiles')}
                        </OcrBatchSecondaryButton>

                        <OcrBatchPrimaryButton
                            variant="contained"
                            color="primary"
                            onClick={handleProcessFiles}
                            disabled={files.length === 0 || isLoading}
                            aria-busy={isLoading}
                            aria-disabled={files.length === 0 || isLoading}
                            aria-label={t('ocr.button.process', {filesCount: (files.length > 0  ? `(${files.length})` : '')})}
                            sx={{ letterSpacing: '1px' }}
                        >
                            {isLoading
                                ? <CircularProgress aria-hidden={true} size={20} color="inherit" />
                                : t('ocr.button.process', {filesCount: files.length > 0 ? `(${files.length})` : '' })}
                        </OcrBatchPrimaryButton>
                    </OcrBatchBox>
                </OcrBatchHeader>

                <OcrBatchContentArea>
                    {files.length === 0 ? (
                        <FileDropZone inputRef={fileInputRef}
                                      isDragging={isDragging}
                                      setIsDragging={setIsDragging}
                                      onDrop={onDrop}
                                      variant="full"
                        />
                    ) : (
                        <OcrBatchFileList role="list">
                            <FileDropZone inputRef={fileInputRef}
                                          isDragging={isDragging}
                                          setIsDragging={setIsDragging}
                                          onDrop={onDrop}
                                          variant="mini"
                            />

                            {files.map(file => (
                                <OcrBatchFileListItem key={file.name}
                                                      role="listitem"
                                                      aria-label={`${file.name}, ${formatOcrFileSize(file.size)}`}
                                >
                                    <OcrBatchFileTypeBadge aria-hidden="true">
                                        {getOcrFileTypeBadge(file)}
                                    </OcrBatchFileTypeBadge>

                                    <OcrBatchFileInfo>
                                        <span className="name" aria-label={t('ocr.aria.fileName', {name: file.name}) as string}>
                                            {file.name}
                                        </span>
                                        <span className="size" aria-label={t('ocr.aria.fileSize', {size: formatOcrFileSize(file.size)}) as string}>
                                            {formatOcrFileSize(file.size)}
                                        </span>
                                    </OcrBatchFileInfo>

                                    {isOcrFilePreviewable(file) && (
                                        <Tooltip title={t('ocr.button.previewFile', {fileName: ''})}>
                                            <IconButton
                                                color="primary"
                                                aria-label={t('ocr.button.previewFile', {fileName: file.name}) as string}
                                                onClick={() => setPreviewFile(file)}
                                            >
                                                <ZoomInIcon sx={{ fontSize: 24 }} />
                                            </IconButton>
                                        </Tooltip>
                                    )}

                                    <Tooltip title={t('ocr.button.removeFile', {fileName: ''})}>
                                        <IconButton
                                            color="error"
                                            aria-label={t('ocr.button.removeFile', {fileName: file.name}) as string}
                                            onClick={() => handleRemoveFile(file.name)}
                                        >
                                            <CloseIcon sx={{ fontSize: 24 }} />
                                        </IconButton>
                                    </Tooltip>
                                </OcrBatchFileListItem>
                            ))}
                        </OcrBatchFileList>
                    )}
                </OcrBatchContentArea>
            </OcrBatchMainContainer>

            {previewFile && (
                <FilePreviewDialog
                    file={previewFile}
                    onClose={() => setPreviewFile(null)}
                />
            )}
        </OcrBatchCard>
    );
};

export default OcrBatch;